import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export default function DateRangePicker(props) {
  // ✅ On récupère les props sans destructuring (pour éviter un cas bizarre de shadowing)
  const value = props?.value;
  const onChangeProp = props?.onChange;

  // ✅ onChange SAFE: si ce n’est pas une fonction, on met une fonction vide
  const safeOnChange = useMemo(() => {
    return typeof onChangeProp === "function" ? onChangeProp : () => {};
  }, [onChangeProp]);

  const [isOpen, setIsOpen] = useState(false);

  const [startDate, setStartDate] = useState(
    value?.from ? new Date(value.from) : null
  );
  const [endDate, setEndDate] = useState(
    value?.to ? new Date(value.to) : null
  );

  const [isMobile, setIsMobile] = useState(false);

  // ✅ Resync si value change
  useEffect(() => {
    setStartDate(value?.from ? new Date(value.from) : null);
    setEndDate(value?.to ? new Date(value.to) : null);
  }, [value?.from, value?.to]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleDateChange = (dates) => {
    const [start, end] = Array.isArray(dates) ? dates : [null, null];

    setStartDate(start);
    setEndDate(end);

    // ✅ Impossible de crasher ici
    safeOnChange({
      from: start ? start.toISOString().slice(0, 16) : "",
      to: end ? end.toISOString().slice(0, 16) : "",
    });

    if (start && end) {
      setTimeout(() => setIsOpen(false), 300);
    }
  };

  const formatDateRange = () => {
    if (!startDate) return "Sélectionnez vos dates";
    if (!endDate) return format(startDate, "dd MMM yyyy", { locale: fr });
    return `${format(startDate, "dd MMM", { locale: fr })} - ${format(
      endDate,
      "dd MMM yyyy",
      { locale: fr }
    )}`;
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="bg-white rounded-xl shadow-lg border border-gray-200 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-3 md:gap-4 hover:shadow-xl transition-all w-full md:w-auto"
      >
        <div className="flex items-center gap-2 md:gap-3">
          <span className="text-xl md:text-2xl">📅</span>
          <div className="text-left">
            <div className="text-[10px] md:text-xs font-semibold text-gray-500 uppercase">
              Dates de voyage
            </div>
            <div className="text-xs md:text-sm font-bold text-gray-900">
              {formatDateRange()}
            </div>
          </div>
        </div>

        <svg
          className="w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 md:p-6 w-full max-w-[95vw] md:max-w-fit animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base md:text-lg font-bold text-gray-900">
                Choisissez vos dates
              </h3>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ✕
              </button>
            </div>

            <div className="flex justify-center">
              <DatePicker
                selected={startDate}
                onChange={handleDateChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                inline
                monthsShown={isMobile ? 1 : 2}
                minDate={new Date()}
                dateFormat="dd/MM/yyyy"
              />
            </div>

            <p className="mt-3 md:mt-4 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5 inline-block">
              💡 Le prix varie selon les jours.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
